import { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Wallet, Search, AlertTriangle, CheckCircle2 } from 'lucide-react';

import { MasterToolbar }       from '@/components/master/MasterToolbar';
import { MasterAuditInfo }     from '@/components/master/MasterAuditInfo';
import { MasterSearchModal }   from '@/components/master/MasterSearchModal';
import type { SearchColumn }   from '@/components/master/MasterSearchModal';
import { MasterReportPreview } from '@/components/master/report/MasterReportPreview';
import { ReportHeader }        from '@/components/master/report/ReportHeader';
import { ReportSection }       from '@/components/master/report/ReportSection';
import { ReportFooter }        from '@/components/master/report/ReportFooter';
import { ConfirmDialog }       from '@/components/ui/ConfirmDialog';
import { Input }               from '@/components/ui/Input';
import { Combobox }            from '@/components/ui/Combobox';
import { Badge }               from '@/components/ui/Badge';
import { Card }                from '@/components/ui/Card';

import { AccountLookupField }  from '@/components/erp/AccountLookupField';

import { useAuth }             from '@/hooks/useAuth';
import { usePermission }       from '@/hooks/usePermission';
import { useMasterScreenState } from '@/hooks/useMasterScreenState';
import { MasterPageHeader } from '@/components/master/MasterPageHeader';
import { MasterLookupBar, MasterCodeLookup } from '@/components/master/MasterLookupBar';
import { Permissions }         from '@/constants/permissions';
import { AppRoutes }           from '@/constants/routes';
import { bankApi, bankBranchApi, bankAccountApi } from '@/api/bank.api';
import type {
  BankAccountDto, BankAccountListDto, BankListDto, BankBranchListDto,
} from '@/types/bank.types';
import { cn } from '@/utils/cn';

// ── Zod schema ─────────────────────────────────────────────────────────────────

const schema = z.object({
  accountNumber:          z.string().min(1, 'Account number is required').max(50),
  accountName:            z.string().min(1, 'Account name is required').max(200),
  bankId:                 z.string().uuid('Bank is required'),
  bankBranchId:           z.string().uuid().nullable().optional(),
  currencyCode:           z.string().length(3, 'Must be 3 characters'),
  accountType:            z.enum(['CURRENT', 'SAVINGS', 'FIXED', 'OVERDRAFT']),
  linkedGLAccountId:      z.string().uuid().nullable().optional(),
  isActive:               z.boolean(),
  reconciliationRequired: z.boolean(),
  allowDirectPosting:     z.boolean(),
  isDefaultPayment:       z.boolean(),
  isDefaultReceipt:       z.boolean(),
  remarks:                z.string().max(500).nullable().optional(),
  defaultCrossingType:    z.string().nullable().optional(),
});

type FormValues = z.infer<typeof schema>;

const EMPTY_FORM: FormValues = {
  accountNumber: '', accountName: '', bankId: '', bankBranchId: null,
  currencyCode: '', accountType: 'CURRENT', linkedGLAccountId: null,
  isActive: true, reconciliationRequired: true, allowDirectPosting: true,
  isDefaultPayment: false, isDefaultReceipt: false, remarks: null,
  defaultCrossingType: null,
};

const TABS = ['General', 'Settings', 'GL Integration', 'Audit'] as const;
type Tab = (typeof TABS)[number];

const ACCOUNT_SEARCH_COLUMNS: SearchColumn<BankAccountListDto>[] = [
  { key: 'accountNumber', label: 'Account No.',  className: 'w-36 font-mono' },
  { key: 'accountName',   label: 'Name' },
  { key: 'bankName',      label: 'Bank',         className: 'w-28', render: r => r.bankName ?? '—' },
  { key: 'currencyCode',  label: 'CCY',          className: 'w-12 font-mono text-center' },
  { key: 'accountType',   label: 'Type',         className: 'w-20' },
  { key: 'isActive',      label: 'Status',       className: 'w-20',
    render: r => <Badge variant={r.isActive ? 'success' : 'default'}>{r.isActive ? 'Active' : 'Inactive'}</Badge> },
];

const BANK_COLUMNS: SearchColumn<BankListDto>[] = [
  { key: 'bankCode', label: 'Code', className: 'w-20 font-mono' },
  { key: 'bankName', label: 'Name' },
];

const BRANCH_COLUMNS: SearchColumn<BankBranchListDto>[] = [
  { key: 'branchCode', label: 'Code', className: 'w-24 font-mono' },
  { key: 'branchName', label: 'Name' },
  { key: 'city',       label: 'City', className: 'w-24', render: r => r.city ?? '—' },
];

function dtoToForm(dto: BankAccountDto): FormValues {
  return {
    accountNumber: dto.accountNumber, accountName: dto.accountName,
    bankId: dto.bankId, bankBranchId: dto.bankBranchId ?? null,
    currencyCode: dto.currencyCode,
    accountType: dto.accountType as 'CURRENT' | 'SAVINGS' | 'FIXED' | 'OVERDRAFT',
    linkedGLAccountId: dto.linkedGLAccountId ?? null,
    isActive: dto.isActive, reconciliationRequired: dto.reconciliationRequired,
    allowDirectPosting: dto.allowDirectPosting, isDefaultPayment: dto.isDefaultPayment,
    isDefaultReceipt: dto.isDefaultReceipt, remarks: dto.remarks ?? null,
    defaultCrossingType: dto.defaultCrossingType ?? null,
  };
}

function fmtDateTime(iso: string | null | undefined): string {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
}

const inputClass = cn(
  'w-full h-9 px-3 rounded-md border text-sm',
  'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
  'border-gray-300 dark:border-gray-600',
  'focus:outline-none focus:ring-2 focus:ring-brand-500',
  'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
);

const ToggleSwitch = ({
  label, description, checked, onChange, disabled,
}: { label: string; description?: string; checked: boolean; onChange: (v: boolean) => void; disabled?: boolean }) => (
  <label className={cn('flex items-start gap-3 py-1', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}>
    <div className="relative mt-0.5 flex-shrink-0">
      <input type="checkbox" checked={checked} onChange={e => onChange(e.target.checked)} disabled={disabled} className="sr-only peer" />
      <div className="w-9 h-5 rounded-full bg-gray-200 dark:bg-gray-600 peer-checked:bg-brand-500 peer-disabled:opacity-50 transition-colors" />
      <div className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform peer-checked:translate-x-4" />
    </div>
    <div>
      <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{label}</span>
      {description && <p className="text-xs text-gray-400 mt-0.5">{description}</p>}
    </div>
  </label>
);

// ── Component ─────────────────────────────────────────────────────────────────

export const BankAccountPage = () => {
  const navigate = useNavigate();
  const { user }          = useAuth();
  const { hasPermission } = usePermission();

  const canView   = hasPermission(Permissions.BNK_BANK_ACCOUNT_VIEW);
  const canCreate = hasPermission(Permissions.BNK_BANK_ACCOUNT_CREATE);
  const canEdit   = hasPermission(Permissions.BNK_BANK_ACCOUNT_EDIT);
  const canDelete = hasPermission(Permissions.BNK_BANK_ACCOUNT_DELETE);

  const screen = useMasterScreenState({ canEdit, canDelete });
  const acctInputRef = useRef<HTMLInputElement>(null);

  const [record,          setRecord]          = useState<BankAccountDto | null>(null);
  const [acctInput,       setAcctInput]       = useState('');
  const [activeTab,       setActiveTab]       = useState<Tab>('General');
  const [searchOpen,      setSearchOpen]      = useState(false);
  const [bankSearchOpen,  setBankSearchOpen]  = useState(false);
  const [branchSearchOpen,setBranchSearchOpen]= useState(false);
  const [saving,          setSaving]          = useState(false);
  const [lookupBusy,      setLookupBusy]      = useState(false);
  const [flashMsg,        setFlashMsg]        = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [printOpen,       setPrintOpen]       = useState(false);

  const [selectedBank,   setSelectedBank]   = useState<{ id: string; code: string; name: string } | null>(null);
  const [selectedBranch, setSelectedBranch] = useState<{ id: string; code: string; name: string } | null>(null);
  const [glAccountDisplay, setGlAccountDisplay] = useState<{ id: string | null; code: string | null; name: string | null }>({
    id: null, code: null, name: null,
  });

  const [pendingSave,       setPendingSave]       = useState<FormValues | null>(null);
  const [showSaveConfirm,   setShowSaveConfirm]   = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showNewConfirm,    setShowNewConfirm]    = useState<string | null>(null);

  const {
    register, handleSubmit, control, reset, setValue,
    formState: { errors },
  } = useForm<FormValues>({ resolver: zodResolver(schema), defaultValues: EMPTY_FORM });


  const flash = (type: 'success' | 'error', text: string) => {
    setFlashMsg({ type, text }); setTimeout(() => setFlashMsg(null), 5000);
  };

  const resetToInitial = useCallback(() => {
    setRecord(null); setAcctInput(''); reset(EMPTY_FORM);
    setSelectedBank(null); setSelectedBranch(null);
    setGlAccountDisplay({ id: null, code: null, name: null });
    screen.setMode('initial'); setActiveTab('General'); setPendingSave(null);
    setTimeout(() => acctInputRef.current?.focus(), 50);
  }, [reset, screen]);

  const loadRecord = useCallback((dto: BankAccountDto) => {
    setRecord(dto); reset(dtoToForm(dto)); setAcctInput(dto.accountNumber);
    setSelectedBank(dto.bankId ? { id: dto.bankId, code: '', name: dto.bankName ?? '' } : null);
    setSelectedBranch(dto.bankBranchId ? { id: dto.bankBranchId, code: '', name: dto.branchName ?? '' } : null);
    setGlAccountDisplay({ id: dto.linkedGLAccountId, code: dto.glAccountCode ?? null, name: dto.glAccountName ?? null });
    screen.setMode('view'); setActiveTab('General');
  }, [reset, screen]);

  const handleAcctLookup = async () => {
    if (!acctInput.trim()) return;
    if (!canView) { flash('error', 'You do not have permission to view bank accounts.'); return; }
    setLookupBusy(true);
    try {
      const res = await bankAccountApi.getList({ search: acctInput.trim(), pageSize: 1 });
      const items = res.data.data?.items ?? [];
      if (items.length > 0) {
        const full = await bankAccountApi.getById(items[0].id);
        if (full.data.data) loadRecord(full.data.data);
      } else {
        if (!canCreate) { flash('error', `Account number '${acctInput}' was not found.`); return; }
        setShowNewConfirm(acctInput.trim());
      }
    } catch { flash('error', 'Lookup failed. Please try again.'); }
    finally { setLookupBusy(false); }
  };

  const handleConfirmCreateNew = () => {
    const acct = showNewConfirm; setShowNewConfirm(null); if (!acct) return;
    reset({ ...EMPTY_FORM, accountNumber: acct, bankId: selectedBank?.id ?? '' });
    setAcctInput(acct); setRecord(null); screen.setMode('new'); setActiveTab('General');
  };

  const handleRefresh   = () => resetToInitial();
  const handleEdit      = () => screen.setMode('edit');
  const handleSaveClick = () => { handleSubmit(v => { setPendingSave(v); setShowSaveConfirm(true); })(); };

  const executeSave = async () => {
    if (!pendingSave) return;
    setShowSaveConfirm(false); setSaving(true);
    try {
      const d = pendingSave;
      if (screen.isNew) {
        await bankAccountApi.create({
          bankId: d.bankId, bankBranchId: selectedBranch?.id ?? null,
          accountNumber: d.accountNumber.trim(), accountName: d.accountName.trim(),
          currencyCode: d.currencyCode.toUpperCase(), accountType: d.accountType,
          linkedGLAccountId: glAccountDisplay.id,
          reconciliationRequired: d.reconciliationRequired, allowDirectPosting: d.allowDirectPosting,
          isDefaultPayment: d.isDefaultPayment, isDefaultReceipt: d.isDefaultReceipt,
          remarks: d.remarks?.trim() || null,
          defaultCrossingType: d.defaultCrossingType || null,
        });
        flash('success', 'Bank account created successfully.');
        resetToInitial();
      } else if (screen.isEdit && record) {
        await bankAccountApi.update(record.id, {
          bankBranchId: selectedBranch?.id ?? null, accountName: d.accountName.trim(),
          currencyCode: d.currencyCode.toUpperCase(), accountType: d.accountType,
          linkedGLAccountId: glAccountDisplay.id, isActive: d.isActive,
          reconciliationRequired: d.reconciliationRequired, allowDirectPosting: d.allowDirectPosting,
          isDefaultPayment: d.isDefaultPayment, isDefaultReceipt: d.isDefaultReceipt,
          remarks: d.remarks?.trim() || null,
          defaultCrossingType: d.defaultCrossingType || null,
        });
        flash('success', 'Bank account updated successfully.');
        resetToInitial();
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { message?: string } } }).response?.data?.message ?? 'Save failed.';
      flash('error', msg);
    } finally { setSaving(false); setPendingSave(null); }
  };

  const handleDelete = () => setShowDeleteConfirm(true);
  const executeDelete = async () => {
    if (!record) return;
    setShowDeleteConfirm(false); setSaving(true);
    try {
      await bankAccountApi.remove(record.id);
      flash('success', `Account '${record.accountNumber}' deleted.`); resetToInitial();
    } catch { flash('error', 'Delete failed.'); }
    finally { setSaving(false); }
  };

  const handlePrint = () => { if (record) setPrintOpen(true); };
  const handleExit  = () => navigate(AppRoutes.DASHBOARD);

  const fetchAccounts = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await bankAccountApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const fetchBanks = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await bankApi.getList({ search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, []);

  const fetchBranches = useCallback(async (search: string, page: number, pageSize: number) => {
    const res = await bankBranchApi.getList({ bankId: selectedBank?.id, search, pageNumber: page, pageSize });
    const d = res.data.data;
    return { items: d?.items ?? [], totalCount: d?.totalCount ?? 0 };
  }, [selectedBank]);

  const handleSearchSelect = (item: BankAccountListDto) => {
    setSearchOpen(false); setLookupBusy(true);
    bankAccountApi.getById(item.id)
      .then(res => { if (res.data.data) loadRecord(res.data.data); })
      .catch(() => flash('error', 'Failed to load account.'))
      .finally(() => setLookupBusy(false));
  };

  const handleBankSelect = (item: BankListDto) => {
    setBankSearchOpen(false);
    setSelectedBank({ id: item.id, code: item.bankCode, name: item.bankName });
    setSelectedBranch(null); // reset branch when bank changes
    // Sync the pick into the form — the hidden input is registered, so its DOM value is ignored;
    // without this, create submitted an empty bankId and was rejected (QA #44).
    setValue('bankId', item.id, { shouldValidate: true });
    setValue('bankBranchId', null);
  };

  const handleBranchSelect = (item: BankBranchListDto) => {
    setBranchSearchOpen(false);
    setSelectedBranch({ id: item.id, code: item.branchCode, name: item.branchName });
    setValue('bankBranchId', item.id);
  };

  const fieldsDisabled = screen.isInitial || screen.isView;

  // ── Render ────────────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col h-full min-h-0">
      <MasterPageHeader
        icon={Wallet}
        title="Bank Account Maintenance"
        subtitle="Manage company bank accounts and GL integration"
        screen={screen}
      />

      <MasterToolbar
        {...screen.toolbar} saving={saving} loading={lookupBusy}
        onSave={handleSaveClick} onEdit={handleEdit} onPrint={handlePrint}
        onDelete={handleDelete} onRefresh={handleRefresh} onExit={handleExit}
      />

      {flashMsg && (
        <div className={cn(
          'flex items-center gap-2 px-5 py-2.5 text-sm',
          flashMsg.type === 'success'
            ? 'bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-300 border-b border-emerald-200 dark:border-emerald-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 border-b border-red-200 dark:border-red-800',
        )}>
          {flashMsg.type === 'success'
            ? <CheckCircle2 size={15} className="flex-shrink-0" />
            : <AlertTriangle size={15} className="flex-shrink-0" />}
          {flashMsg.text}
        </div>
      )}

      <MasterLookupBar
        label="Account No."
        summary={record && (
          <>
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">{record.accountName}</span>
            <span className="font-mono text-xs text-gray-400">{record.currencyCode}</span>
            <Badge variant="default">{record.accountType}</Badge>
            {!record.isActive && (
              <span className="px-1.5 py-0.5 rounded bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-[10px] font-bold">Inactive</span>
            )}
          </>
        )}
        hint={screen.isInitial && 'Enter account number and press Enter, or browse.'}
      >
        <MasterCodeLookup
          inputRef={acctInputRef}
          value={acctInput}
          onChange={setAcctInput}
          onSubmit={handleAcctLookup}
          onBrowse={() => setSearchOpen(true)}
          browseLabel="Search bank accounts"
          placeholder="Account number + Enter…"
          // 50, matching account_number. The shared default is 20, which is a master CODE; an
          // account number is a real bank account number and routinely longer.
          maxLength={50}
          disabled={screen.isNew || screen.isEdit || saving}
          busy={lookupBusy}
        />
      </MasterLookupBar>

      {/* Main content */}
      <div className="flex-1 overflow-auto">
        <form onSubmit={e => e.preventDefault()} noValidate>
          <div className="flex gap-0 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-5 sticky top-0 z-10">
            {TABS.map(tab => (
              <button key={tab} type="button" onClick={() => setActiveTab(tab)}
                className={cn(
                  'px-4 py-2.5 text-xs font-medium border-b-2 transition-colors whitespace-nowrap',
                  activeTab === tab
                    ? 'border-brand-500 text-brand-600 dark:text-brand-400'
                    : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200',
                )}
              >{tab}</button>
            ))}
          </div>

          <div className="p-5 space-y-5 max-w-2xl">

            {activeTab === 'General' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Account Details</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <Input label="Account Number" {...register('accountNumber')} disabled
                    placeholder="— enter via lookup bar above —" className="font-mono" />
                  <Input label="Account Name *" {...register('accountName')} error={errors.accountName?.message}
                    disabled={fieldsDisabled} placeholder="e.g. Main Operating Account" />

                  {/* Bank lookup */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Bank *</label>
                    <div className={cn('flex items-center h-9 rounded-md border', fieldsDisabled
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800')}>
                      <span className="flex-1 px-3 text-sm text-gray-700 dark:text-gray-300 truncate">
                        {selectedBank ? `${selectedBank.code} — ${selectedBank.name}` : record?.bankName ?? 'None selected'}
                      </span>
                      {!fieldsDisabled && (
                        <button type="button" onClick={() => setBankSearchOpen(true)}
                          className="flex-shrink-0 w-8 h-full flex items-center justify-center border-l border-gray-200 dark:border-gray-600 rounded-r-md text-gray-400 hover:text-brand-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <Search size={13} />
                        </button>
                      )}
                    </div>
                    {errors.bankId && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.bankId.message}</p>}
                    <input type="hidden" {...register('bankId')} value={selectedBank?.id ?? record?.bankId ?? ''} />
                  </div>

                  {/* Branch lookup */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Branch</label>
                    <div className={cn('flex items-center h-9 rounded-md border', fieldsDisabled
                      ? 'border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50'
                      : 'border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800')}>
                      <span className="flex-1 px-3 text-sm text-gray-700 dark:text-gray-300 truncate">
                        {selectedBranch ? `${selectedBranch.code} — ${selectedBranch.name}` : record?.branchName ?? '— None —'}
                      </span>
                      {selectedBranch && !fieldsDisabled && (
                        <button type="button" onClick={() => setSelectedBranch(null)}
                          className="flex-shrink-0 w-7 h-7 flex items-center justify-center text-gray-400 hover:text-rose-500">
                          ✕
                        </button>
                      )}
                      {!fieldsDisabled && (
                        <button type="button" onClick={() => setBranchSearchOpen(true)}
                          className="flex-shrink-0 w-8 h-full flex items-center justify-center border-l border-gray-200 dark:border-gray-600 rounded-r-md text-gray-400 hover:text-brand-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                          <Search size={13} />
                        </button>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Currency *</label>
                    <input {...register('currencyCode')} disabled={fieldsDisabled || screen.isEdit}
                      maxLength={3} placeholder="e.g. LKR"
                      className={cn(inputClass, 'font-mono uppercase')} />
                    {errors.currencyCode && <p className="mt-1 text-xs text-red-600 dark:text-red-400">{errors.currencyCode.message}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Account Type</label>
                    <Controller name="accountType" control={control} render={({ field }) => (
                      <Combobox
                        value={field.value}
                        onChange={field.onChange}
                        disabled={fieldsDisabled}
                        clearable={false}
                        options={[
                          { value: 'CURRENT',   label: 'CURRENT'        },
                          { value: 'SAVINGS',   label: 'SAVINGS'        },
                          { value: 'FIXED',     label: 'FIXED DEPOSIT'  },
                          { value: 'OVERDRAFT', label: 'OVERDRAFT'      },
                        ]}
                      />
                    )} />
                  </div>

                  {(screen.isEdit || screen.isView) && (
                    <div className="sm:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                      <Controller name="isActive" control={control} render={({ field }) => (
                        <ToggleSwitch label={field.value ? 'Active' : 'Inactive'} checked={field.value}
                          onChange={field.onChange} disabled={fieldsDisabled} />
                      )} />
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Settings' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Account Settings</h3>
                <div className="space-y-1">
                  {([
                    ['reconciliationRequired', 'Reconciliation Required',       'Bank reconciliation must be performed for this account.'],
                    ['allowDirectPosting',     'Allow Direct Posting',           'Allows direct GL posting without going through banking module.'],
                    ['isDefaultPayment',       'Default Payment Account',        'Pre-selected for outgoing payment transactions.'],
                    ['isDefaultReceipt',       'Default Receipt Account',        'Pre-selected for incoming receipt transactions.'],
                  ] as [keyof FormValues, string, string][]).map(([fieldName, label, desc]) => (
                    <Controller key={fieldName} name={fieldName} control={control} render={({ field }) => (
                      <ToggleSwitch label={label} description={desc}
                        checked={field.value as boolean} onChange={field.onChange} disabled={fieldsDisabled} />
                    )} />
                  ))}
                </div>
                {/* ── Default cheque crossing (GAP-007) ─────────────────── */}
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Default Cheque Crossing
                  </label>
                  <Controller name="defaultCrossingType" control={control} render={({ field }) => (
                    <Combobox
                      value={field.value ?? ''}
                      onChange={v => field.onChange(v || '')}
                      disabled={fieldsDisabled}
                      placeholder="Not stated — nothing prints"
                      options={[
                        { value: 'ACCOUNT_PAYEE',  label: 'A/C Payee Only'     },
                        { value: 'NOT_NEGOTIABLE', label: 'Not Negotiable'     },
                        { value: 'OPEN',           label: 'Open (no crossing)' },
                      ]}
                    />
                  )} />
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Applied to cheques a voucher issues from this account. It can still be changed
                    on an individual print run.
                  </p>
                </div>

                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Remarks</label>
                  <textarea {...register('remarks')} disabled={fieldsDisabled} rows={3} maxLength={500}
                    placeholder="Optional notes…"
                    className={cn(
                      'w-full px-3 py-2 rounded-md border text-sm resize-none',
                      'bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100',
                      'border-gray-300 dark:border-gray-600',
                      'focus:outline-none focus:ring-2 focus:ring-brand-500',
                      'disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed',
                    )} />
                </div>
              </Card>
            )}

            {activeTab === 'GL Integration' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">General Ledger Integration</h3>
                <div className="space-y-4">
                  <AccountLookupField
                    label="Linked GL Account"
                    value={glAccountDisplay.id}
                    displayCode={glAccountDisplay.code}
                    displayName={glAccountDisplay.name}
                    onChange={(id, code, name) => setGlAccountDisplay({ id, code, name })}
                    disabled={fieldsDisabled}
                    hint="Link this bank account to a GL cash/bank account for automatic postings."
                  />
                  {glAccountDisplay.id && (
                    <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 text-[11px] text-emerald-700 dark:text-emerald-400">
                      <div className="font-semibold">GL Account Linked</div>
                      <div className="mt-0.5 font-mono">{glAccountDisplay.code} — {glAccountDisplay.name}</div>
                      <div className="mt-1 text-emerald-600 dark:text-emerald-500">
                        Transactions will automatically post to this GL account.
                      </div>
                    </div>
                  )}
                  {!glAccountDisplay.id && (
                    <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-[11px] text-amber-700 dark:text-amber-400">
                      No GL account linked. Bank transactions will require manual GL journal entries.
                    </div>
                  )}
                </div>
              </Card>
            )}

            {activeTab === 'Audit' && (
              <Card>
                <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-4">Audit Information</h3>
                {record ? (
                  <MasterAuditInfo createdBy={record.createdBy} createdByName={record.createdByName} createdDate={record.createdDate}
                    modifiedBy={record.modifiedBy} modifiedByName={record.modifiedByName} modifiedDate={record.modifiedDate} />
                ) : (
                  <p className="text-sm text-gray-400 italic">Audit information will appear after the record is saved.</p>
                )}
              </Card>
            )}
          </div>
        </form>
      </div>

      {/* Modals */}
      <MasterSearchModal<BankAccountListDto>
        isOpen={searchOpen} onClose={() => setSearchOpen(false)}
        title="Browse Bank Accounts" columns={ACCOUNT_SEARCH_COLUMNS}
        fetchData={fetchAccounts} onSelect={handleSearchSelect}
      />
      <MasterSearchModal<BankListDto>
        isOpen={bankSearchOpen} onClose={() => setBankSearchOpen(false)}
        title="Select Bank" columns={BANK_COLUMNS}
        fetchData={fetchBanks} onSelect={handleBankSelect}
      />
      <MasterSearchModal<BankBranchListDto>
        isOpen={branchSearchOpen} onClose={() => setBranchSearchOpen(false)}
        title="Select Branch" columns={BRANCH_COLUMNS}
        fetchData={fetchBranches} onSelect={handleBranchSelect}
      />

      <ConfirmDialog isOpen={showNewConfirm !== null} variant="info"
        title="Create New Bank Account"
        message={<span>Account <strong className="font-mono">{showNewConfirm}</strong> does not exist. Create a new record?</span>}
        confirmLabel="Yes, Create" cancelLabel="No"
        onConfirm={handleConfirmCreateNew} onCancel={() => setShowNewConfirm(null)}
      />
      <ConfirmDialog isOpen={showSaveConfirm} variant="save"
        title={screen.isNew ? 'Save New Bank Account' : 'Save Changes'}
        message={screen.isNew ? 'Save this new bank account?' : `Save changes to "${record?.accountName}"?`}
        confirmLabel="Yes, Save" cancelLabel="No" loading={saving}
        onConfirm={executeSave} onCancel={() => { setShowSaveConfirm(false); setPendingSave(null); }}
      />
      <ConfirmDialog isOpen={showDeleteConfirm} variant="delete"
        title="Delete Bank Account"
        message={<span>Delete account <strong>{record?.accountNumber} — {record?.accountName}</strong>?<br /><span className="text-xs text-gray-400">Soft-delete only.</span></span>}
        confirmLabel="Yes, Delete" cancelLabel="No" loading={saving}
        onConfirm={executeDelete} onCancel={() => setShowDeleteConfirm(false)}
      />

      {record && (
        <MasterReportPreview isOpen={printOpen} onClose={() => setPrintOpen(false)} title="Bank Account Report">
          <ReportHeader reportTitle="Bank Account Report"
            printedBy={user?.fullName ?? user?.username} printedAt={new Date()}
            meta={[
              { label: 'Account No.', value: record.accountNumber },
              { label: 'Currency',    value: record.currencyCode },
            ]}
          />
          <ReportSection title="Account Details" fields={[
            { label: 'Account Number', value: record.accountNumber },
            { label: 'Account Name',   value: record.accountName, wide: true },
            { label: 'Bank',           value: record.bankName },
            { label: 'Branch',         value: record.branchName },
            { label: 'Currency',       value: record.currencyCode },
            { label: 'Account Type',   value: record.accountType },
            { label: 'Status',         value: record.isActive, display: record.isActive ? 'Active' : 'Inactive' },
          ]} />
          <ReportSection title="Settings" fields={[
            { label: 'Reconciliation Required', value: record.reconciliationRequired, display: record.reconciliationRequired ? 'Yes' : 'No' },
            { label: 'Allow Direct Posting',    value: record.allowDirectPosting, display: record.allowDirectPosting ? 'Yes' : 'No' },
            { label: 'Default Payment',         value: record.isDefaultPayment, display: record.isDefaultPayment ? 'Yes' : 'No' },
            { label: 'Default Receipt',         value: record.isDefaultReceipt, display: record.isDefaultReceipt ? 'Yes' : 'No' },
            { label: 'GL Account',              value: record.glAccountCode ? `${record.glAccountCode} — ${record.glAccountName}` : null },
          ]} />
          <ReportSection title="Audit Information" fields={[
            { label: 'Created By',    value: record.createdByName ?? record.createdBy },
            { label: 'Created Date',  value: fmtDateTime(record.createdDate) },
            { label: 'Modified By',   value: record.modifiedByName ?? record.modifiedBy },
            { label: 'Modified Date', value: fmtDateTime(record.modifiedDate) },
          ]} />
          <ReportFooter />
        </MasterReportPreview>
      )}
    </div>
  );
};
